<template>
  <app-dialog :value="value" :max-width="560" nested blur @input="onDialogInput">
    <template #title>Uhrzeiten der Fahrt {{ trip ? trip.trip_id : "" }}</template>
    <p v-if="state === 'loading'" class="ttd__state" role="status">Wird geladen …</p>
    <div v-else-if="state === 'error'" class="ttd__state ttd__state--error" role="alert">
      Die Haltestellen konnten nicht geladen werden.
      <app-button kind="tertiary" @click="load">Erneut versuchen</app-button>
    </div>
    <p v-else-if="!rows.length" class="ttd__state">Keine Haltestellen.</p>
    <ol v-else class="ttd__list">
      <li v-for="(row, index) in rows" :key="row.order_nr" class="ttd__row">
        <span class="ttd__nr" aria-hidden="true">{{ index + 1 }}</span>
        <label class="ttd__name" :for="fieldId(row)">{{ row.name || "–" }}</label>
        <app-text-field
          :id="fieldId(row)"
          class="ttd__time"
          :value="drafts[row.order_nr]"
          inputmode="numeric"
          placeholder="hh:mm"
          :error="errors[row.order_nr]"
          :disabled="saving"
          @input="setTime(row, $event)"
        />
      </li>
    </ol>
    <p v-if="saveError" class="app-field__error ttd__error" role="alert">
      <app-icon name="alert" :size="14" />{{ saveError }}
    </p>
    <template #actions>
      <span v-if="changed.length" class="ttd__count" aria-live="polite">{{ changeText }}</span>
      <app-button :disabled="saving" @click="close">Abbrechen</app-button>
      <app-button kind="primary" icon="check" :disabled="!changed.length || hasErrors" :loading="saving" @click="save">
        Speichern
      </app-button>
    </template>
  </app-dialog>
</template>

<script>
// Uhrzeiten einer Shop-Fahrt bearbeiten (Kundenwunsch 13.10.2026, Prüfung auf alte Stellen: vorher ShopTimetableDataGrid
// mit Filterzeile, DevExtreme-Seitenleiste und Bearbeiten in der Tabelle mit der Speicherleiste von DevExtreme): Liste
// der Halte in Fahrtreihenfolge mit Nummer, Ort und Uhrzeit „hh:mm“ als Feld; „Speichern“ erst bei Änderungen und
// gültigen Zeiten, „n Änderungen“ daneben. Daten wie bisher: GET shoptimetable?trip_id&version_id, je geänderter
// Halt PUT shoptimetable?trip_id&version_id&order_nr mit { start } (Sekunden wie gespeichert, sonst „:00“).
// v-model = offen; Ereignis saved nach dem Speichern (die Seite lädt Fahrten und Halte neu).
import axios from "axios";

const TIME = /^([01]?\d|2[0-3]):([0-5]\d)$/;

// „06:15:00“ → „06:15“
function short(value) {
  return value ? String(value).substr(0, 5) : "";
}

export default {
  name: "ShopTimetableDialog",
  props: {
    value: { type: Boolean, default: false },
    // Fahrt (trip_id, version_id)
    trip: { type: Object, default: null },
  },
  data() {
    return {
      state: "idle",
      rows: [],
      drafts: {},
      errors: {},
      saving: false,
      saveError: "",
    };
  },
  computed: {
    changed() {
      return this.rows.filter((row) => (this.drafts[row.order_nr] || "") !== short(row.start));
    },
    changeText() {
      const n = this.changed.length;
      return n === 1 ? "1 Änderung" : n + " Änderungen";
    },
    hasErrors() {
      return Object.keys(this.errors).length > 0;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(open) {
        if (open) {
          this.load();
        }
      },
    },
  },
  methods: {
    fieldId(row) {
      return "ttd-" + this._uid + "-" + row.order_nr;
    },
    async load() {
      if (!this.trip) {
        return;
      }
      this.state = "loading";
      this.saveError = "";
      this.errors = {};
      try {
        const response = await axios.get(
          `${this.$store.state.baseUrl}shoptimetable?trip_id=${this.trip.trip_id}&version_id=${this.trip.version_id}`
        );
        const rows = Array.isArray(response.data) ? response.data : [];
        this.rows = rows.slice().sort((a, b) => (Number(a.order_nr) || 0) - (Number(b.order_nr) || 0));
        const drafts = {};
        this.rows.forEach((row) => {
          drafts[row.order_nr] = short(row.start);
        });
        this.drafts = drafts;
        this.state = "ready";
      } catch (e) {
        this.state = "error";
      }
    },
    setTime(row, value) {
      const text = String(value || "").trim();
      this.drafts = Object.assign({}, this.drafts, { [row.order_nr]: text });
      const errors = Object.assign({}, this.errors);
      if (text && !TIME.test(text)) {
        errors[row.order_nr] = "Bitte als hh:mm eingeben.";
      } else if (!text) {
        errors[row.order_nr] = "Pflichtfeld";
      } else {
        delete errors[row.order_nr];
      }
      this.errors = errors;
      this.saveError = "";
    },
    // „6:5“ gibt es nicht (Prüfung), „6:05“ → „06:05“; Sekunden wie bisher gespeichert, sonst „:00“
    apiTime(row) {
      const m = TIME.exec(this.drafts[row.order_nr]);
      const hhmm = m[1].padStart(2, "0") + ":" + m[2];
      const seconds = row.start && String(row.start).length > 5 ? String(row.start).substr(5) : ":00";
      return hhmm + seconds;
    },
    async save() {
      if (!this.changed.length || this.hasErrors || this.saving) {
        return;
      }
      this.saving = true;
      this.saveError = "";
      try {
        for (const row of this.changed) {
          await axios.put(
            `${this.$store.state.baseUrl}shoptimetable?trip_id=${row.trip_id}&version_id=${row.version_id}&order_nr=${row.order_nr}`,
            { start: this.apiTime(row) }
          );
        }
      } catch (e) {
        this.saving = false;
        this.saveError = "Die Uhrzeiten konnten nicht gespeichert werden.";
        this.load();
        return;
      }
      this.saving = false;
      this.$emit("saved");
      this.close();
    },
    close() {
      if (!this.saving) {
        this.$emit("input", false);
      }
    },
    onDialogInput(open) {
      if (!open) {
        this.close();
      }
    },
  },
};
</script>

<style scoped>
.ttd__list {
  margin: 0;
  padding: 0;
  list-style: none;
  border: 1px solid var(--line);
  border-radius: var(--r-ctl);
}
.ttd__row {
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr) 112px;
  align-items: center;
  gap: 12px;
  padding: 6px 12px;
  border-bottom: 1px solid var(--line-2);
}
.ttd__row:last-child {
  border-bottom: 0;
}
.ttd__nr {
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-3);
  font-variant-numeric: tabular-nums;
}
.ttd__name {
  min-width: 0;
  font-size: 13px;
  line-height: 20px;
  color: var(--ink);
  overflow-wrap: anywhere;
}
.ttd__time ::v-deep input {
  font-variant-numeric: tabular-nums;
}
p.ttd__state,
.ttd__state {
  margin: 0;
  font-size: 13px;
  line-height: 20px;
  color: var(--ink-3);
}
.ttd__state--error {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  color: var(--red);
}
p.ttd__error {
  margin-top: 12px;
}
.ttd__count {
  margin-right: auto;
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-2);
}
</style>
